<template>
  <div class="flex flex-row items-center gap-3 pr-2">
    <CustomDropdown
      ref="profileDropdown"
      maxHeight="90vh"
      gap="10px"
      position="end"
      dropdownCSS="p-0"
      subsection-trigger-selector=".theme-item"
      subsection-direction="right"
      @subsection-open="subsectionOpened"
    >
      <template #trigger>
        <div
          class="flex flex-row items-center justify-center gap-1 cursor-pointer"
          v-on:click.prevent
        >
          <div
            :class="['profile_circle', authUserProfileColor.class].filter(Boolean)"
            :style="authUserProfileColor.style"
          >
            <span class="text-sm font-normal text-white">
              {{ userInitials.toUpperCase() }}
            </span>
          </div>
        </div>
      </template>

      <template #default>
        <div
          id="nav_dropdown_header"
          class="flex flex-row gap-2 p-4 items-center border_bottom"
        >
          <ProfileIcon :data="currentUserProfile || userEmail" />
          <span class="text-sm font-medium text_color">{{ userEmail }}</span>
        </div>

        <div
          v-if="workspaces.length > 0 && billingSetupComplete"
          class="px-2 py-3 border_bottom flex flex-col gap-2"
        >
          <span class="text-[11px] opacity-60 font-semibold px-3 text_color"
            >WORKSPACES</span
          >

          <div
            class="flex flex-col gap-1 overflow-y-scroll"
            style="max-height: calc(90vh - 460px)"
          >
            <div
              v-for="workspace in workspaces"
              :key="workspace.id"
              class="workspace_item"
              :class="{
                workspace_item_selected:
                  workspace.id === Number(currentWorkspaceId),
              }"
              @click="switchWorkspace(workspace.id)"
            >
              <div class="icon_placeholder">
                <ThemeImage
                  v-if="workspace.id === Number(currentWorkspaceId)"
                  alt="selected"
                  class="size-[14px]"
                  :icon="imageNames.TICK_GREY"
                />
              </div>

              <div
                class="workspace_rect"
                :class="getAuthUserProfileColor(workspace).class"
                :style="getAuthUserProfileColor(workspace).style"
              >
                <ThemeImage
                  v-if="startsWithSpecialChar(workspace.name)"
                  :icon="imageNames.WORKSPACE_SPECIAL_CHAR_ICON"
                  class="size-4"
                  alt="workspace_icon"
                />
                <template v-else>{{ getInitials(workspace.name) }}</template>
              </div>

              <span class="text_color">{{ workspace.name }}</span>
            </div>
          </div>

          <div
            class="px-3 py-1 font-medium text-sm flex flex-row gap-2 items-center cursor-pointer"
            @click="showNewWorkspaceModal"
          >
            <ThemeImage
              class="size-[14px]"
              :icon="imageNames.ADD_GREY"
              alt="add_icon"
            />
            <span class="text_color">Create New Workspace</span>
          </div>
        </div>

        <div id="nav_dropdown_footer" class="p-2">
          <div id="billing_section" v-if="billingSetupComplete">
            <div
              v-if="billingVersion === 'v1' || billingVersion === 'v3' || billingVersion === 'v4'"
              class="secondary_color text-[13px] font-semibold ml-2 mt-2"
            >
              Billing Overview
            </div>

            <div
              v-if="billingVersion === 'v4'"
              class="text-sm px-2 py-2 secondary_color"
            >
              <span class="font-semibold text_color">{{ remainingAgentCredits.toLocaleString() }} </span> credits remaining
            </div>

            <BillingProgress
              class="mt-3 mb-3"
              v-if="billingVersion === 'v1' || billingVersion === 'v3'"
              :remaining="remainingAgentCredits"
              :billing-status="props.billingStatus"
              :user-count="userCount"
              :human-seats="humanSeats"
              :total="totalAgentCredits"
              :billing-version="billingVersion"
            />

            <div
              v-for="item in billingItems"
              :key="item.key"
              class="dropdown_item"
              @click="item.action"
            >
              <div class="flex items-center w-full gap-2">
                <ThemeImage
                  class="size-4"
                  :icon="item.icon"
                  :alt="`${item.text.toLowerCase()}_icon`"
                />
                <span class="text_color">{{ item.text }}</span>
              </div>
              <img
                class="size-5"
                :src="imagePath.neutral.grey.linkGreyIcon"
                alt="check_icon"
              />
            </div>
          </div>

          <div
            v-for="item in settingsLogoutItems"
            :key="item.key"
            class="dropdown_item"
            @click="item.action"
          >
            <div class="flex items-center w-full gap-2">
              <ThemeImage
                class="size-4"
                :icon="item.icon"
                :alt="`${item.text.toLowerCase()}_icon`"
                :style="{ opacity: item.icon_opacity || 1 }"
              />
              <span class="text_color">{{ item.text }}</span>
            </div>
          </div>
        </div>
      </template>
    </CustomDropdown>

    <CustomModal
      title="Create Workspace"
      v-model:visible="newWorkspaceModalVisible"
      :width="'32vw'"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium color_666">Workspace name</span>
          <a-input
            ref="workspaceInputRef"
            v-model:value="workspaceName"
            class="input_medium"
            placeholder="Enter workspace name"
            :status="errors.workspaceName ? 'error' : ''"
            @blur="validateWorkspaceName"
            @pressEnter="handleWorkspaceCreated"
            @input="
              () => {
                if (errors.workspaceName) validateWorkspaceName();
              }
            "
          />
          <div v-if="errors.workspaceName" class="error_message_color text-xs">
            {{ errors.workspaceName }}
          </div>
        </div>
      </div>
      <template #footer>
        <a-button
          class="primary_medium"
          @click="handleWorkspaceCreated"
          :loading="creatingWorkspace"
        >
          Create Workspace
        </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { message } from "ant-design-vue";
import { onMounted, onUnmounted, ref, watch, nextTick, computed } from "vue";
import {
  getInitials,
  getAuthUserProfileColor,
  getProfileColor,
  handleUsernameInitials,
  logout,
  disconnectMqtt,
} from "@/src/utils/utils.js";
import CustomModal from "@/src/components/CustomModal.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import DashboardService from "@/src/api/DashboardService.js";
import BillingService from "@/src/api/BillingService.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import eventBus from "@/src/utils/eventBus.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { useRouter } from "vue-router";
import store from "@/src/store/index.js";
import { fetchPermissions } from "@/src/utils/permissionUtils.js";
import { permissionConstants } from "@/src/constants/permissionConstants.js";
import { moveToBilling } from "@/src/utils/utils.js";
import BillingProgress from "@/src/components/navbar_components/BillingProgress.vue";
import { initializeIntercom } from "@/src/utils/pluginUtils.js";
import { useIntercom } from "@/src/composables/useIntercom.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { themeTypes } from "@/src/constants/themeConstants.js";
import imagePath from "@/src/paths/imagePath";

const props = defineProps({
  onboardingInProgress: { type: Boolean, required: true },
  billingVersion: { type: String, default: "v0" },
  billingStatus: { type: String, default: "" },
});

const emit = defineEmits(["update-current-workspace"]);

const intercom = useIntercom();
const router = useRouter();
const workspaces = ref([]);
const currentWorkspaceId = ref(null);
const profileDropdown = ref(null);
const userName = ref("Major Pandey Singh");
const userEmail = ref("");
const workspaceName = ref("");
const newWorkspaceModalVisible = ref(false);
const userCount = ref(1);
const humanSeats = ref(1);
const creatingWorkspace = ref(false);
const userInitials = ref(handleUsernameInitials());
const currentUserProfile = ref(null);
const remainingAgentCredits = ref(0);
const totalAgentCredits = ref(0);
const workspaceInputRef = ref(null);

const errors = ref({
  workspaceName: "",
});

const loadingWebsiteDomain = ref(false);

const authUserProfileColor = computed(() =>
  getAuthUserProfileColor(currentUserProfile.value || {})
);

const billingSetupComplete = computed(
  () => store.getters["billing/isBillingSetupComplete"]
);
const permissions = computed(() => {
  try {
    const stored = localStorage.getItem("userPermissions");
    if (!stored || stored === "null") return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
});

const startsWithSpecialChar = (name) => {
  if (!name) return false;
  return !/^[a-zA-Z0-9]/.test(name);
};

const subsectionOpened = () => {};

const validateWorkspaceName = () => {
  if (!workspaceName.value.trim()) {
    errors.value.workspaceName = "Enter workspace name to continue";
    return false;
  }
  errors.value.workspaceName = "";
  return true;
};

const validateForm = () => {
  return validateWorkspaceName();
};

const moveToBusinessDetail = () => {
  try {
    profileDropdown.value?.closeDropdown();
    router.push({ path: "/settings", hash: "#general" });
  } catch (error) {
    console.error(
      "An error occurred while moving to business details: ",
      error
    );
  }
};

const updateUserInitials = () => {
  try {
    userInitials.value = handleUsernameInitials();
  } catch (error) {
    console.error("An error occurred while updating user initials: ", error);
  }
};

const showNewWorkspaceModal = () => {
  try {
    errors.value.workspaceName = "";
    workspaceName.value = "";
    newWorkspaceModalVisible.value = true;
  } catch (error) {
    console.error(
      "An error occurred while showing new workspace modal: ",
      error
    );
  }
};

const billingItems = computed(() => {
  if (
    (props.billingVersion === "v1" ||
      props.billingVersion === "v3" ||
      props.billingVersion === "v4") &&
    permissions.value.includes(permissionConstants.BILLING)
  ) {
    return [
      {
        key: "billing",
        action: () => goToBilling(false),
        icon: imageNames.BILLING_GREY,
        text: "Manage Billing",
      },
    ];
  }
  return [];
});

const refer = () => {
  router.push({ path: "/refer" });
};

const handleActionClick = (action) => {
  try {
    if (action?.action && typeof action.action === "function") {
      try {
        action.action();
      } catch (error) {
        console.error(`Error executing action for ${action.id}:`, error);
      }
    }
  } catch (error) {
    console.error("An error occurred while handling action click: ", error);
  }
};

const triggerBeamer = () => {
  try {
    if (window.beamer && typeof window.beamer.show === "function") {
      window.beamer.show();
    } else if (window.Beamer && typeof window.Beamer.show === "function") {
      window.Beamer.show();
    } else {
      console.log("Beamer not loaded or not available");
      const beamerElement = document.querySelector(".beamerTrigger");
      if (beamerElement) {
        beamerElement.click();
      } else {
        console.log("No beamer trigger element found");
      }
    }
  } catch (error) {
    console.error("Error triggering beamer:", error);
  }
};

const talkToOnboardingAssistant = () => {
  try {
    window.open("https://sales.superagi.com/meet/superagi-onboarding-assistant", "_blank", "noopener,noreferrer");
    profileDropdown.value?.closeDropdown();
  } catch (error) {
    console.error("Error opening onboarding assistant:", error);
  }
};

const talkToSalesAssistant = () => {
  try {
    window.open("https://sales.superagi.com/meet/superagi-sales", "_blank", "noopener,noreferrer");
    profileDropdown.value?.closeDropdown();
  } catch (error) {
    console.error("Error opening sales assistant:", error);
  }
};

const settingsLogoutItems = computed(() => {
  const items = [];

  items.push({
    key: "settings",
    action: moveToBusinessDetail,
    icon: imageNames.SETTINGS_GREY,
    text: "Settings",
  });

  items.push({
    key: "whats_new",
    action: triggerBeamer,
    icon: imageNames.WHATS_NEW,
    text: "What's New?",
    icon_opacity: 0.5,
  });

  items.push({
    key: "onboarding_assistant",
    action: talkToOnboardingAssistant,
    icon: imageNames.AGI_ASSISTANT_LIGHTNING,
    text: "Talk to Onboarding Assistant",
  });

  items.push({
    key: "sales_assistant",
    action: talkToSalesAssistant,
    icon: imageNames.MESSAGE,
    text: "Talk to Sales Assistant",
  });

  items.push({
    key: "refer",
    action: refer,
    icon: imageNames.REFER_GREY,
    text: "Refer a business",
  });

  items.push({
    key: "logout",
    action: logout,
    icon: imageNames.LOGOUT_GREY,
    text: "Logout",
  });

  return items;
});

const goToBilling = (creditsPack = false) => {
  try {
    moveToBilling(creditsPack, props.billingVersion);
    profileDropdown.value?.closeDropdown();
  } catch (error) {
    console.error("An error occurred while going to billing: ", error);
  }
};

const handleWorkspaceCreated = async () => {
  try {
    if (!validateForm()) {
      return;
    }

    creatingWorkspace.value = true;
    const response = await DashboardService.createWorkspace({
      name: workspaceName.value,
    });
    if (response.success) {
      workspaces.value = response.workspaces;
      await switchWorkspace(response.workspace.id);

      message.success("Workspace created successfully");
      workspaceName.value = "";
      errors.value.workspaceName = "";
      newWorkspaceModalVisible.value = false;
    } else {
      message.error("Failed to create workspace");
    }
  } catch (error) {
    console.error("Error creating workspace:", error);
    if (error.response.status === 400) {
      message.error(error.response.data.error);
    } else {
      message.error("An error occurred while creating the workspace");
    }
  } finally {
    creatingWorkspace.value = false;
  }
};

const fetchWorkspaces = async () => {
  try {
    const response = await DashboardService.fetchWorkspaces();

    if (response?.success) {
      await store.dispatch("auth/setUserData", response?.profile || {});
      await store.dispatch("theme/initTheme", response?.profile?.theme);
      store.commit("chats/SET_CHAT_NOTIFICATION_SOUND_ENABLED", !response?.profile?.chat_notifications_muted);

      workspaces.value = response.workspaces;
      currentUserProfile.value = response?.profile || null;

      initializeIntercom(response.profile, intercom);

      const firstName = response.profile.first_name || "";
      const lastName = response.profile.last_name || "";
      if (firstName || lastName) {
        userName.value = `${firstName} ${lastName}`.trim();
        localStorage.setItem("userName", userName.value);
      } else {
        userName.value = "";
      }

      userEmail.value = response.profile.email;
      localStorage.setItem("userEmail", response.profile?.email);
      localStorage.setItem("userId", response.profile?.id);
      userInitials.value = handleUsernameInitials();

      if (workspaces.value?.length > 0) {
        currentWorkspaceId.value = WorkspaceService.getCurrentWorkspaceId();
        if (DashboardService.isDefined(currentWorkspaceId.value)) {
          let workspace = workspaces.value?.find(
            (item) => item.id === Number(currentWorkspaceId.value)
          );

          await store.dispatch(
            "workspace/setIsPhoneNumberVerified",
            workspace?.is_phone_number_verified || false
          );

          if (workspace?.workspace_meeting_link) {
            localStorage.setItem(
              "workspaceMeetingLink",
              workspace?.workspace_meeting_link
            );
          } else localStorage.removeItem("workspaceMeetingLink");

          const isCurrentWorkspaceValid = workspaces.value.some(
            (workspace) =>
              workspace.id.toString() === currentWorkspaceId.value.toString()
          );
          if (!isCurrentWorkspaceValid) {
            WorkspaceService.removeCurrentWorkspaceId();
            currentWorkspaceId.value = undefined;
          }
        }
        if (!DashboardService.isDefined(currentWorkspaceId.value)) {
          await switchWorkspace(response.default_workspace_id);
        }
      }
    } else {
      console.error("Failed to fetch workspaces");
      await store.dispatch("theme/initTheme", themeTypes.LIGHT);
    }
  } catch (error) {
    console.error("Error fetching workspaces:", error);
    await store.dispatch("theme/initTheme", themeTypes.LIGHT);
  }
};

const switchWorkspace = async (workspaceId) => {
  await store.dispatch("tasks/resetAutoPlayState");
  await store.dispatch("dynamicBanner/updateShowBanner", false);
  
  await disconnectMqtt();
  
  WorkspaceService.setCurrentWorkspaceId(workspaceId);

  localStorage.removeItem("userPermissions");
  localStorage.removeItem("userRoleId");

  window.location.href = `${window.location.origin}/i-assistant?workspace_id=${workspaceId}`;
};

const getTotalUsage = async () => {
  try {
    const response = await BillingService.getTotalUsage();
    if (response?.success) {
      remainingAgentCredits.value = Math.floor(response.remaining_agent_credits);
      totalAgentCredits.value = response.total_agent_credits;
    } else {
      console.error("Failed to fetch total usage");
    }
  } catch (error) {
    console.error("An error occurred while fetching total usage:", error);
  }
};
const getUserUsage = async () => {
  try {
    const response = await DashboardService.fetchUsers();
    const pendingResponse = await DashboardService.fetchPendingUsers();
    const res = await BillingService.getTotalSeats();
    let pendingInviteCount = 0;
    if (response?.success && pendingResponse?.success) {
      pendingInviteCount = pendingResponse?.users?.filter(
        (user) => user.pending_invite
      ).length;
      userCount.value = response.total_active + pendingInviteCount;
    }
    if (res?.success) {
      humanSeats.value = res.human_seats;
      userCount.value = humanSeats.value - userCount.value;
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

watch(currentWorkspaceId, (newValue) => {
  if (newValue) {
    nextTick(() => {
      emit("update-current-workspace");
    });
  }
});

watch(
  () => profileDropdown.value?.isOpen,
  (newVal) => {
    if (newVal) {
      getTotalUsage();
      getUserUsage();
    }
  }
);

watch(
  () => newWorkspaceModalVisible.value,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        workspaceInputRef.value?.focus();
      });
    }
  }
);

const refreshOnBillingSetupComplete = () => {
  getUserUsage();
  getTotalUsage();
};

onMounted(() => {
  getUserUsage();

  // Always fetch permissions on mount to ensure they're current for this workspace
  fetchPermissions();

  fetchWorkspaces();
  getTotalUsage();
  eventBus.on("refresh-workspace-name", fetchWorkspaces);
  eventBus.on("user-name-updated", updateUserInitials);
  eventBus.on("billing-setup-complete", refreshOnBillingSetupComplete);
});

onUnmounted(() => {
  eventBus.off("user-name-updated", updateUserInitials);
  eventBus.off("billing-setup-complete", refreshOnBillingSetupComplete);
});
</script>

<style scoped>
.workspace_item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
}

.workspace_item_selected,
.workspace_item:hover {
  border-radius: 8px;
  background: var(--white-opacity-8);
}

.workspace_rect {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  width: 28px;
  height: 28px;
  font-size: 12px;
}

.active_submenu_item {
  background: var(--white-opacity-8);
}
</style>
